import createClient from 'openapi-fetch';
import type { Middleware } from 'openapi-fetch';
import i18n from 'i18next';
import type { paths } from './schema';
import { notifyPermissionDenied } from '../lib/notifications';
import { apiTraceMiddleware } from '../lib/apiTrace';

export const apiBaseUrl: string = import.meta.env.VITE_API_BASE_URL || '';

console.log('apiBaseUrl:', apiBaseUrl);

export const apiClient = createClient<paths>({
  baseUrl: apiBaseUrl
});


export type ApiClient = typeof apiClient;

// Every org-scoped route is permission-gated server-side (spec 2026-09-04-02).
// A denial answers 403 with `{title, code: "permission_denied", permission}`.
// Surface it ONCE, here, as the standard non-blocking notification: call sites
// must never render the raw error string, and a page firing several denied
// requests must not produce several banners (the bus collapses by key).
//
// Registered at module scope and never ejected — setAuthToken() only swaps the
// auth middleware, which is a different registration.
apiClient.use({
  async onResponse({ response }) {
    if (response.status !== 403) return response;
    try {
      // Clone: the caller still needs to read the body itself.
      const body = await response.clone().json();
      if (body && body.code === 'permission_denied') {
        notifyPermissionDenied(
          typeof body.permission === 'string' ? body.permission : undefined
        );
      }
      // Two-step verification (spec 2026-10-01-03): the session must set up
      // or verify a second factor. AuthContext listens for this event
      // (MFA_EVENT) and opens the matching full-screen page.
      if (body && (body.code === 'mfa_enrollment_required' || body.code === 'mfa_required')) {
        const org = /\/api\/v1\/organizations\/([^/]+)\//.exec(new URL(response.url, window.location.origin).pathname)?.[1];
        window.dispatchEvent(new CustomEvent('buildtory:mfa', { detail: { code: body.code, org } }));
      }
    } catch {
      // Not JSON, or already consumed — nothing useful to say, and this must
      // never turn a 403 into a thrown error.
    }
    return response;
  },
});

// Records every call for the in-app feedback report's Timeline section
// (spec 2026-09-06-16) — never a request/response body, never a query
// string, no header beyond X-Request-ID. See lib/apiTrace.ts. Same
// module-scope, never-ejected contract as the 403 middleware above.
apiClient.use(apiTraceMiddleware);

// The language the user picked in the app, not the browser's: the API renders
// generated text (issue titles, rule labels on the site map) in the reader's
// language (spec 2026-10-25-20), and a French user on an English browser must
// read French. i18next is a singleton, so this reads the live choice.
apiClient.use({
  onRequest({ request }) {
    const lang = i18n.resolvedLanguage || i18n.language;
    if (lang) request.headers.set('Accept-Language', lang);
    return request;
  },
});

// Store the middleware reference so we can remove it later
// (openapi-fetch's `use()` returns nothing: `eject()` takes the middleware object itself.)
let authMiddleware: Middleware | null = null;

// The bearer token of the current session, for callers that cannot go through
// openapi-fetch (XMLHttpRequest uploads with progress).
let currentAuthToken: string | null = null;
export const getAuthToken = (): string | null => currentAuthToken;

// Mirror of the backend's CookieAuthToken constant (auth/handler.go).
// Resources loaded by <iframe>/<img>/<video src=…> can't carry an Authorization
// header, so they rely on this cookie. The backend sets it on /auth/login and
// /auth/refresh, but we re-set it here on every token change so it stays in
// sync even when the page is rehydrated from localStorage or the Set-Cookie
// response was lost in flight.
//
// Its max-age follows the token's own `exp` claim (spec 2026-10-11-31): a
// fixed hour let a token rehydrated from localStorage, already half or fully
// expired, sit in the cookie long after the server stopped accepting it, and
// the document preview then rendered the 401 body as the document.
const ACCESS_TOKEN_COOKIE = 'access_token';
const ACCESS_TOKEN_COOKIE_MAX_AGE = 3600; // fallback for tokens without exp (PATs)

// tokenExpiry returns the `exp` claim (seconds since epoch) of a JWT, or null
// when the token isn't a decodable JWT (a PAT, an email token).
export const tokenExpiry = (token: string): number | null => {
  const parts = token.split('.');
  if (parts.length !== 3) return null;
  try {
    const b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4);
    const payload = JSON.parse(atob(padded)) as { exp?: unknown };
    return typeof payload.exp === 'number' ? payload.exp : null;
  } catch {
    return null;
  }
};

// cookieMaxAgeFor is the cookie lifetime for a token: the seconds left until
// its `exp` (0 once expired, which deletes the cookie), or the fallback hour
// when the token carries no expiry.
export const cookieMaxAgeFor = (token: string, nowMs: number = Date.now()): number => {
  const exp = tokenExpiry(token);
  if (exp === null) return ACCESS_TOKEN_COOKIE_MAX_AGE;
  return Math.max(0, Math.floor(exp - nowMs / 1000));
};

const setAuthCookie = (token: string) => {
  if (typeof document === 'undefined') return;
  document.cookie = `${ACCESS_TOKEN_COOKIE}=${token}; path=/; max-age=${cookieMaxAgeFor(token)}; samesite=lax`;
};

const clearAuthCookie = () => {
  if (typeof document === 'undefined') return;
  document.cookie = `${ACCESS_TOKEN_COOKIE}=; path=/; max-age=0; samesite=lax`;
};

// The session's refresh lives in AuthContext (it owns the refresh token and
// the auth state). It registers itself here so code outside React — the
// document preview's fetch, ensureAuthCookie — can renew the access token.
// The registered function must end with setAuthToken(newToken) on success.
type TokenRefresher = () => Promise<unknown>;
let tokenRefresher: TokenRefresher | null = null;
let inflightRefresh: Promise<string | null> | null = null;

export const registerTokenRefresher = (fn: TokenRefresher | null) => {
  tokenRefresher = fn;
};

// refreshAuthToken renews the access token through the registered refresher
// and resolves with the new one. Concurrent callers share one refresh: a page
// with several previews failing at once must not burn the refresh token
// several times. Rejects when the refresh fails.
export const refreshAuthToken = (): Promise<string | null> => {
  if (!tokenRefresher) return Promise.resolve(currentAuthToken);
  if (!inflightRefresh) {
    const refresher = tokenRefresher;
    inflightRefresh = (async () => {
      try {
        await refresher();
        return currentAuthToken;
      } finally {
        inflightRefresh = null;
      }
    })();
  }
  return inflightRefresh;
};

// ensureAuthCookie makes the access_token cookie hold a token the server will
// accept for at least minValiditySeconds: it refreshes a token that expires
// sooner, then rewrites the cookie from the current token. Call it before
// handing a URL to an element that authenticates by cookie (<video>,
// <audio>, a download link). Never rejects: a failed refresh leaves the
// current token in place and the caller's own error handling takes over.
export const ensureAuthCookie = async (minValiditySeconds = 60): Promise<string | null> => {
  const token = currentAuthToken;
  if (!token) return null;
  const exp = tokenExpiry(token);
  if (exp !== null && exp * 1000 - Date.now() < minValiditySeconds * 1000) {
    try {
      await refreshAuthToken();
    } catch {
      // Reported by the caller's own failure path.
    }
  }
  if (currentAuthToken) setAuthCookie(currentAuthToken);
  return currentAuthToken;
};

// fetchWithAuth GETs a URL with the session's bearer token instead of the
// cookie. On a 401 it refreshes the token once and retries; the second answer
// is returned whatever it is, so the caller decides how to show a failure.
export const fetchWithAuth = async (url: string, init: RequestInit = {}): Promise<Response> => {
  const send = (token: string | null) => {
    const headers = new Headers(init.headers);
    if (token) headers.set('Authorization', `Bearer ${token}`);
    return fetch(url, { ...init, headers });
  };
  const first = await send(await ensureAuthCookie());
  if (first.status !== 401 || !tokenRefresher) return first;
  let renewed: string | null;
  try {
    renewed = await refreshAuthToken();
  } catch {
    return first;
  }
  return send(renewed);
};

export const setAuthToken = (token: string) => {
  // Remove existing auth middleware if any
  if (authMiddleware) {
    apiClient.eject(authMiddleware);
  }

  // Add new auth middleware
  authMiddleware = {
    onRequest({ request }) {
      request.headers.set('Authorization', `Bearer ${token}`);
      return request;
    },
  };
  apiClient.use(authMiddleware);

  currentAuthToken = token;
  setAuthCookie(token);
};

export const removeAuthToken = () => {
  if (authMiddleware) {
    apiClient.eject(authMiddleware);
    authMiddleware = null;
  }
  currentAuthToken = null;
  clearAuthCookie();
};
